// medplum-protocols.js — care-template PROTOCOLS as real FHIR PlanDefinition
// resources, read + managed browser-direct AS THE LOGGED-IN USER (main-2).
//
// A protocol is a PlanDefinition (title + ordered action[]; each action may link an
// ActivityDefinition order — the wiring native $apply follows). It is OWNED by one
// DEPARTMENT (its HOD curates it) OR is HOSPITAL-WIDE (admin curates it, every doctor
// sees it). The owner is carried two FHIR-native, SEARCHABLE ways (the SAME contract
// the seeder scripts/medplum/seed-care-templates.mjs writes):
//   1. useContext — code 'focus', valueCodeableConcept { system PROTOCOL_DEPT_SYSTEM,
//      code <dept-slug> | 'hospital-wide' }. The base R4 `context` search param makes
//      the picker filter a plain FHIR search:  PlanDefinition?context=<sys>|<slug>.
//   2. extension owning-department -> valueReference(Organization) — the durable ref
//      the management UI + the manage-protocol write-auth Bot read (absent = hospital-wide).
//
// READ (picker): every doctor has PlanDefinition readonly (opd-doctor-rowlevel), so
// listProtocolsForDepartment reads DIRECTLY. WRITE (management UI): routed through the
// manage-protocol Bot (medplum.executeBot) — the Bot re-derives the editor's own
// department + HOD status server-side and REFUSES a cross-department or non-HOD edit
// (admin bypass). The browser NEVER writes a PlanDefinition directly.
import { getMedplum } from './medplum-client.js';

// ── Shared systems (MUST match seed-care-templates.mjs + manage-protocol.cjs) ──
export const PROTOCOL_DEPT_SYSTEM = 'https://healther.io/opd/protocol-department'; // useContext code system
export const OWNING_DEPT_EXT = 'https://healther.io/opd/owning-department';        // PlanDefinition.extension
export const HOSPITAL_WIDE_CODE = 'hospital-wide';
const USAGE_CONTEXT_TYPE = { system: 'http://terminology.hl7.org/CodeSystem/usage-context-type', code: 'focus', display: 'Clinical Focus' };
const PLANDEF_TYPE = { coding: [{ system: 'http://terminology.hl7.org/CodeSystem/plan-definition-type', code: 'clinical-protocol', display: 'Clinical Protocol' }] };

// The owning-department slug of a PlanDefinition (from useContext), or
// HOSPITAL_WIDE_CODE when hospital-wide. Null when it carries no dept context (a
// legacy / hand-authored PlanDefinition) — the picker treats those as hospital-wide.
export function protocolDepartmentSlug(pd) {
  for (const u of (pd.useContext || [])) {
    for (const c of (u.valueCodeableConcept?.coding || [])) {
      if (c.system === PROTOCOL_DEPT_SYSTEM && c.code) return c.code;
    }
  }
  return null;
}

export function isHospitalWide(pd) {
  const slug = protocolDepartmentSlug(pd);
  return slug === HOSPITAL_WIDE_CODE || slug === null;
}

// Does this PlanDefinition look like one of OUR care-template protocols? True when
// it carries the clinical-protocol type coding OR our department/hospital-wide
// carrier (useContext PROTOCOL_DEPT_SYSTEM). Used to filter the admin management
// list's broad fallback so unrelated PlanDefinitions never surface there.
const PLANDEF_TYPE_CODE = PLANDEF_TYPE.coding[0].code; // 'clinical-protocol'
export function isCareTemplateProtocol(pd) {
  const hasTypeCoding = (pd?.type?.coding || []).some(
    (c) => c.code === PLANDEF_TYPE_CODE && (!c.system || c.system === PLANDEF_TYPE.coding[0].system),
  );
  if (hasTypeCoding) return true;
  return protocolDepartmentSlug(pd) !== null; // carries our dept/hospital-wide context
}

// The owning-department Organization reference (Organization/<uuid>) or null.
export function owningDepartmentRef(pd) {
  const ext = (pd.extension || []).find((e) => e.url === OWNING_DEPT_EXT);
  return ext?.valueReference?.reference || null;
}

// Shape a PlanDefinition into the compact DTO the picker + management list render.
export function toProtocolDTO(pd) {
  const slug = protocolDepartmentSlug(pd);
  const hw = isHospitalWide(pd);
  return {
    id: pd.id,
    url: pd.url,
    title: pd.title || pd.name || '(untitled protocol)',
    status: pd.status || 'active',
    departmentSlug: hw ? null : slug,
    departmentRef: owningDepartmentRef(pd),
    departmentName: (pd.extension || []).find((e) => e.url === OWNING_DEPT_EXT)?.valueReference?.display
      || (pd.useContext || []).flatMap((u) => u.valueCodeableConcept?.coding || []).find((c) => c.system === PROTOCOL_DEPT_SYSTEM)?.display
      || (hw ? 'Hospital-wide' : slug),
    hospitalWide: hw,
    actions: (pd.action || []).map((a) => ({ title: a.title || '', definitionCanonical: a.definitionCanonical || '' })),
  };
}

// NEWER of two PlanDefinitions wins (so an edit/re-seed shows, not a stale copy).
// Ordered by meta.lastUpdated, then FHIR version string.
function isNewer(a, b) {
  const ta = Date.parse(a?.meta?.lastUpdated || '') || 0;
  const tb = Date.parse(b?.meta?.lastUpdated || '') || 0;
  if (ta !== tb) return ta > tb;
  return String(a?.version || '') > String(b?.version || '');
}

// Collapse a raw PlanDefinition list to one entry per logical protocol, NEWEST wins.
// TWO passes, defence-in-depth on top of an idempotent seeder:
//   1. by canonical `url` — the seeder's idempotency key (one protocol = one url);
//      this also folds the same resource returned by BOTH context searches.
//   2. by normalised `title` + owning-department slug — the last-resort guard that
//      also collapses OUT-OF-BAND residue (test/verify harnesses mint DISTINCT
//      per-run urls for the same-titled protocol, so pass 1 alone would still show
//      N copies). Same title + same owner = one row. `version` is folded into the
//      newest-wins comparison, not the key, so two versions of one protocol collapse.
// Legacy PlanDefinitions with no url flow straight to pass 2 (keyed by title+slug).
function collapseProtocols(list) {
  const byUrl = new Map();
  const noUrl = [];
  for (const pd of (list || [])) {
    if (!pd || !pd.id) continue;
    if (pd.url) {
      const prev = byUrl.get(pd.url);
      if (!prev || isNewer(pd, prev)) byUrl.set(pd.url, pd);
    } else {
      noUrl.push(pd);
    }
  }
  const byTitleDept = new Map();
  for (const pd of [...byUrl.values(), ...noUrl]) {
    const title = (pd.title || pd.name || '').trim().toLowerCase();
    const key = `${title}|${protocolDepartmentSlug(pd) || ''}`;
    const prev = byTitleDept.get(key);
    if (!prev || isNewer(pd, prev)) byTitleDept.set(key, pd);
  }
  return [...byTitleDept.values()];
}

// ── READ: the picker's list (own department + hospital-wide) ──────────────────
// listProtocolsForDepartment(slug) returns the active protocols a doctor in
// department `slug` may apply: OWN-department-owned OR hospital-wide. Two searches
// (Medplum has no OR across two `context` values in one query cleanly). Collapsed
// by canonical url (title+version fallback), NEWEST wins — so neither the two
// searches overlapping NOR out-of-band residue (test harnesses minting distinct
// per-run urls for the same protocol) ever shows a protocol twice. A doctor with no
// department (admin/reception) gets hospital-wide only.
export async function listProtocolsForDepartment(departmentSlug) {
  const { medplum } = await getMedplum();
  const raw = [];

  // Hospital-wide (always) — searchable by the distinguished context code.
  raw.push(...await medplum.searchResources('PlanDefinition',
    `status=active&context=${encodeURIComponent(PROTOCOL_DEPT_SYSTEM + '|' + HOSPITAL_WIDE_CODE)}&_count=200`));

  if (departmentSlug) {
    raw.push(...await medplum.searchResources('PlanDefinition',
      `status=active&context=${encodeURIComponent(PROTOCOL_DEPT_SYSTEM + '|' + departmentSlug)}&_count=200`));
  }

  return collapseProtocols(raw).map(toProtocolDTO).sort((a, b) => a.title.localeCompare(b.title));
}

// ── READ: the management list (what the current user may manage) ──────────────
// An HOD sees ONLY their own department's protocols. Admin sees ALL (every
// department + hospital-wide). The picker read above already covers "own dept +
// hospital-wide"; management is intentionally narrower for an HOD (they manage,
// not apply), so it does NOT include hospital-wide for a plain HOD (admin-owned).
export async function listManageableProtocols({ isAdmin, departmentSlug } = {}) {
  const { medplum } = await getMedplum();
  if (isAdmin) {
    const all = await medplum.searchResources('PlanDefinition', 'type=clinical-protocol&_count=500');
    // Fall back to a broad read if the type filter misses legacy resources, but
    // FILTER in JS to only PlanDefinitions that are actually ours — a care-template
    // protocol carries the clinical-protocol type coding OR our dept/hospital-wide
    // carrier (useContext PROTOCOL_DEPT_SYSTEM). Without this, an unrelated
    // PlanDefinition in the project would surface in the management UI.
    const list = all.length ? all : (await medplum.searchResources('PlanDefinition', '_count=500')).filter(isCareTemplateProtocol);
    return list.map(toProtocolDTO).sort(sortByDeptThenTitle);
  }
  if (!departmentSlug) return [];
  const own = await medplum.searchResources('PlanDefinition',
    `context=${encodeURIComponent(PROTOCOL_DEPT_SYSTEM + '|' + departmentSlug)}&_count=200`);
  return own.map(toProtocolDTO).sort(sortByDeptThenTitle);
}

function sortByDeptThenTitle(a, b) {
  const da = a.hospitalWide ? '' : (a.departmentName || a.departmentSlug || '~');
  const db = b.hospitalWide ? '' : (b.departmentName || b.departmentSlug || '~');
  return da.localeCompare(db) || a.title.localeCompare(b.title);
}

// ── READ: the hospital-wide ActivityDefinition catalogue (order building blocks) ──
// The management form offers these as the "linked order" for an action.
export async function listActivityDefinitions() {
  const { medplum } = await getMedplum();
  const list = await medplum.searchResources('ActivityDefinition', 'status=active&_count=200');
  return list.map((ad) => ({
    url: ad.url,
    label: ad.code?.text || ad.code?.coding?.[0]?.display || ad.title || ad.name || ad.url,
    kind: ad.kind || '',
  })).sort((a, b) => a.label.localeCompare(b.label));
}

// ── WRITE: create / edit / retire — via the manage-protocol Bot (run privileged,
// requester-derived dept + HOD guard). The browser never PUTs a PlanDefinition. ──
async function execManageBot(input) {
  const { medplum, cfg } = await getMedplum();
  const botRef = cfg && cfg.bots && cfg.bots.manageProtocol;
  if (!botRef) throw new Error('manage-protocol Bot not configured (cfg.bots.manageProtocol missing — rerun the deploy/seed)');
  const out = await medplum.executeBot(botRef, input);
  const res = (out && out.returnValue) || out;
  if (res && res.authorized === false) {
    const err = new Error(`protocol ${input.op} not authorized${res.reason ? ': ' + res.reason : ''}`);
    err.authorized = false; err.reason = res.reason;
    throw err;
  }
  if (!res || res.ok !== true) throw new Error(`protocol ${input.op} failed${res && res.error ? ': ' + res.error : ''}`);
  return res;
}

// Create a NEW department-owned (HOD) or hospital-wide (admin) protocol.
// The Bot IGNORES a cross-department `departmentSlug` for an HOD (locks to their
// own dept); admin may pass any slug or 'hospital-wide'. `actions` = [{ title,
// definitionCanonical? }].
export function createProtocol({ title, departmentSlug, actions }) {
  return execManageBot({ op: 'create', title, departmentSlug: departmentSlug || null, actions: actions || [] });
}

// Edit an existing protocol (title + actions; a plain HOD cannot move it to
// another department — the Bot refuses). id = PlanDefinition id.
export function editProtocol({ id, title, actions }) {
  return execManageBot({ op: 'edit', id, title, actions: actions || [] });
}

// Retire a protocol (status -> retired; it drops out of the active picker). Kept,
// not deleted, so applied CarePlans keep their instantiatesCanonical lineage.
export function retireProtocol({ id }) {
  return execManageBot({ op: 'retire', id });
}

export const _internals = { USAGE_CONTEXT_TYPE, PLANDEF_TYPE, collapseProtocols, isNewer };
